// Voice library (pages/models.tsx, components/modals/ModelSearchPageV2) and
// the category hubs (/ai-voices/[slug], components/hub/CategoryHub).
// See messages/README.md for how to add strings.
//
// Category variables, filled by utils/categoryNames.ts:
// - {category}: English title-cased badge ("Public Figure"); es/pt the name
//   as it reads mid-sentence ("famosos").
// - {categoryLower}: English lower-cased; es/pt same as {category}.
// - {of}: es/pt only, "de famosos" / "en japonés" (languages take en/em).
// library.cat.<slug> / library.lang.<slug> name a badge by its URL slug;
// badges without one (mostly show and brand names) keep their own name.
export const library = {
  en: {
    "library.of": "{name}",
    "library.ofLanguage": "{name}",

    // SEO (pages/models.tsx and ModelSearchPageV2)
    "library.seo.title": "AI Voice Library{filter} | Jammable",
    "library.seo.titleFiltered": "{filter} AI Voice Library | Jammable",
    "library.seo.description":
      "Browse 93,000+ AI voice models. Create AI covers with voices from anime, gaming, cartoon, K-pop, and more. New voices added daily.",
    "library.seo.ogDescription":
      "Browse 93,000+ AI voice models. Create AI covers with voices from anime, gaming, cartoon, K-pop, and more.",
    "library.seo.listDescription":
      "Jammable AI Voices. AI Cover Song Maker. Text To Speech. Most Advanced AI Technology. 50,000+ AI Cover Voices.",
    "library.seo.listDescriptionFiltered":
      "{filter} AI Voices. {filter} AI Cover Song Maker. Text To Speech. Most Advanced AI Technology. 50,000+ AI Cover Voices.",
    "library.crumb.home": "Home",
    "library.crumb.library": "AI Voice Library",
    "library.crumb.filter": "{category} Voices",

    // ModelSearchPageV2
    "library.title": "Voice library",
    "library.subtitle":
      "93,000+ voices. Tap one to hear it, or make a cover straight away.",
    "library.train": "Train your own voice",
    "library.search": "Search voices",
    "library.searchIn": "Search {categoryLower} voices",
    "library.categoriesNav": "Voice categories",
    "library.all": "All",
    "library.moreCategories": "More categories",
    "library.fewerCategories": "Fewer categories",
    "library.collections": "Collections",
    "library.collections.prev": "Previous collections",
    "library.collections.next": "More collections",
    "library.collection.voices": "{n} voices",
    "library.voicesIn": "{category} voices",
    "library.allVoices": "All voices",
    "library.empty.title": "No voices found",
    "library.empty.body": "Try another name, or train the voice yourself.",
    "library.loadMore": "Load more voices",

    // components/voices/VoiceSortTabs
    "library.sort.aria": "Sort voices",
    "library.sort.hot": "Trending today",
    "library.sort.hot.short": "Trending",
    "library.sort.top": "Most used",
    "library.sort.top.short": "Most used",
    "library.sort.liked": "Most liked",
    "library.sort.liked.short": "Most liked",
    "library.sort.new": "Newest",
    "library.sort.new.short": "Newest",

    // CategoryHub
    "library.hub.title":
      "{category} AI Voices — Make AI Covers With {category} Voices | Jammable",
    "library.hub.titlePage": "{category} AI Voices, Page {page} | Jammable",
    "library.hub.description":
      "Browse {count}+ {category} AI voices on Jammable. Pick any voice to make an AI cover or use the AI voice changer.",
    "library.hub.descriptionPage":
      "Page {page} of {lastPage} of {category} AI voices on Jammable, ranked by uses. Pick any voice to make an AI cover or use the AI voice changer.",
    "library.hub.name": "{category} AI Voices",
    "library.hub.crumbVoices": "AI Voices",
    "library.hub.voices": "Voices",
    "library.hub.page": "Page {page}",
    "library.hub.h1": "{category} AI voices",
    "library.hub.intro":
      "Browse {count}+ {category} AI voices. Pick any one to make an AI cover, or use it as a voice changer.",
    "library.hub.popular": "Popular {categoryLower} voices",
    "library.hub.mostUsedPage":
      "Most used {categoryLower} voices, page {page} of {lastPage}",
    "library.hub.noMatch": "No {categoryLower} voices match that search.",
    "library.hub.openLibrary": "Open in the voice library",
    "library.hub.moreCategories": "Explore more categories",
    "library.hub.faq": "Frequently asked questions",
    "library.hub.keepExploring": "Keep exploring",
    "library.hub.moreWays": "More ways to create with Jammable.",
    "library.hub.link.aiCover": "AI Cover Generator",
    "library.hub.link.voiceChanger": "AI Voice Changer",
    "library.hub.link.allVoices": "Browse all AI voices",
    "library.hub.pages": "Pages",
    "library.hub.first": "First",
    "library.hub.prev": "Previous",
    "library.hub.next": "Next",
    "library.hub.last": "Last",
    "library.hub.faq1.q": "What are {category} AI voices?",
    "library.hub.faq1.a":
      "{category} AI voices are voice models in the {categoryLower} category that you can use to make AI covers or change your own voice. Jammable has {count}+ of them, with new voices added every day.",
    "library.hub.faq2.q": "How do I use a {category} AI voice?",
    "library.hub.faq2.a":
      "Open any {category} voice, add audio you have the rights to (or record your own), and press generate. Jammable converts the vocals into that voice and returns a finished cover in seconds.",
    "library.hub.faq3.q": "How many {category} voices are there?",
    "library.hub.faq3.a":
      "Jammable currently has {count}+ {category} AI voices, and the library grows daily as the community uploads and trains new ones.",

    // Category names. English shows the badge itself; these document the keys.
    "library.cat.public-figure": "Public Figure",
    "library.cat.music": "Music",
    "library.cat.cartoon": "Cartoon",
    "library.cat.gaming": "Gaming",
    "library.cat.fictional": "Fictional",
    "library.cat.anime": "Anime",
    "library.cat.influencer": "Influencer",
    "library.cat.singer": "Singer",
    "library.cat.comedy": "Comedy",
    "library.cat.pop": "Pop",
    "library.cat.youtuber": "Youtuber",
    "library.cat.tv-show": "Tv Show",
    "library.cat.rap": "Rap",
    "library.cat.fictional-character": "Fictional Character",
    "library.cat.movie": "Movie",
    "library.cat.movies": "Movies",
    "library.cat.funny": "Funny",
    "library.cat.kpop": "Kpop",
    "library.cat.k-pop": "K-pop",
    "library.cat.parody": "Parody",
    "library.cat.animation": "Animation",
    "library.cat.animated": "Animated",
    "library.cat.rock": "Rock",
    "library.cat.vtuber": "Vtuber",
    "library.cat.fantasy": "Fantasy",
    "library.cat.game": "Game",
    "library.cat.entertainment": "Entertainment",
    "library.cat.rapper": "Rapper",
    "library.cat.horror": "Horror",
    "library.cat.actor": "Actor",
    "library.cat.actors": "Actors",
    "library.cat.actress": "Actress",
    "library.cat.meme": "Meme",
    "library.cat.action": "Action",
    "library.cat.educational": "Educational",
    "library.cat.hip-hop-rap": "Hip-hop/rap",
    "library.cat.hip-hop": "Hip-hop",
    "library.cat.adventure": "Adventure",
    "library.cat.tv": "Tv",
    "library.cat.female": "Female",
    "library.cat.male": "Male",
    "library.cat.superhero": "Superhero",
    "library.cat.drama": "Drama",
    "library.cat.sci-fi": "Sci-fi",
    "library.cat.science-fiction": "Science Fiction",
    "library.cat.alternative": "Alternative",
    "library.cat.metal": "Metal",
    "library.cat.technology": "Technology",
    "library.cat.indie": "Indie",
    "library.cat.celebrity": "Celebrity",
    "library.cat.fiction": "Fiction",
    "library.cat.american": "American",
    "library.cat.british": "British",
    "library.cat.australian": "Australian",
    "library.cat.latino": "Latino",
    "library.cat.mexico": "Mexico",
    "library.cat.villain": "Villain",
    "library.cat.politics": "Politics",
    "library.cat.president": "President",
    "library.cat.animal": "Animal",
    "library.cat.animals": "Animals",
    "library.cat.dog": "Dog",
    "library.cat.cat": "Cat",
    "library.cat.sports": "Sports",
    "library.cat.wrestling": "Wrestling",
    "library.cat.streamer": "Streamer",
    "library.cat.country": "Country",
    "library.cat.robot": "Robot",
    "library.cat.sound-effects": "Sound Effects",
    "library.cat.historical": "Historical",
    "library.cat.folk": "Folk",
    "library.cat.r-b": "R&b",
    "library.cat.rnb": "Rnb",
    "library.cat.jazz": "Jazz",
    "library.cat.reggaeton": "Reggaeton",
    "library.cat.electronic": "Electronic",
    "library.cat.pop-music": "Pop Music",
    "library.cat.comic": "Comic",
    "library.cat.demon": "Demon",
    "library.cat.news": "News",
    "library.cat.artist": "Artist",
    "library.cat.artistas": "Artistas",
    "library.cat.cantantes": "Cantantes",
    "library.cat.cantante": "Cantante",
    "library.cat.personajes": "Personajes",
    "library.cat.character": "Character",
    "library.cat.singing": "Singing",
    "library.cat.mystery": "Mystery",
    "library.cat.christmas": "Christmas",
    "library.cat.sitcom": "Sitcom",
    "library.cat.pirate": "Pirate",
    "library.cat.toy": "Toy",
    "library.cat.classic": "Classic",
    "library.cat.art": "Art",
    "library.cat.poetry": "Poetry",
    "library.cat.food": "Food",
    "library.cat.fashion": "Fashion",
    "library.cat.family": "Family",
    "library.cat.podcast": "Podcast",
    "library.cat.radio": "Radio",
    "library.cat.ai": "Ai",
    "library.cat.artificial-intelligence": "Artificial Intelligence",
    "library.cat.song": "Song",
    "library.cat.ringtone": "Ringtone",
    "library.cat.web-series": "Web-series",
    "library.cat.idol": "Idol",
    "library.cat.dub": "Dub",
    "library.cat.80s": "80s",
    "library.cat.indie-games": "Indie Games",
    "library.cat.game-show": "Game Show",
    "library.lang.japanese": "Japanese",
    "library.lang.japenese": "Japenese",
    "library.lang.korean": "Korean",
    "library.lang.spanish": "Spanish",
    "library.lang.english": "English",
    "library.lang.french": "French",
    "library.lang.italian": "Italian",
    "library.lang.german": "German",
    "library.lang.deutsch": "Deutsch",
    "library.lang.russian": "Russian",
    "library.lang.hebrew": "Hebrew",
    "library.lang.chinese": "Chinese",
    "library.lang.arabic": "Arabic",
    "library.lang.swedish": "Swedish",
    "library.lang.polish": "Polish",
    "library.lang.turkish": "Turkish",
    "library.lang.finnish": "Finnish",
    "library.lang.greek": "Greek",
    "library.lang.indonesian": "Indonesian",
    "library.lang.thai": "Thai",
    "library.lang.hungarian": "Hungarian",
    "library.lang.filipino": "Filipino",
    "library.lang.malaysian": "Malaysian",
    "library.lang.portuguese": "Portuguese",
    "library.lang.brasileiro": "Brasileiro",
    "library.lang.pt-br": "Pt Br",
    "library.lang.romanian": "Romanian",
    "library.lang.hindi": "Hindi",
    "library.lang.dutch": "Dutch",
    "library.lang.czech": "Czech",
    "library.lang.vietnamese": "Vietnamese",
  },
  es: {
    "library.of": "de {name}",
    "library.ofLanguage": "en {name}",

    "library.seo.title": "Biblioteca de voces IA{filter} | Jammable",
    "library.seo.titleFiltered": "Biblioteca de voces IA - {filter} | Jammable",
    "library.seo.description":
      "Explora más de 93,000 voces IA. Haz covers con IA con voces de anime, videojuegos, caricaturas, K-pop y más. Cada día se agregan voces nuevas.",
    "library.seo.ogDescription":
      "Explora más de 93,000 voces IA. Haz covers con IA con voces de anime, videojuegos, caricaturas, K-pop y más.",
    "library.seo.listDescription":
      "Explora más de 93,000 voces IA. Haz covers con IA con voces de anime, videojuegos, caricaturas, K-pop y más. Cada día se agregan voces nuevas.",
    "library.seo.listDescriptionFiltered":
      "Voces IA {of} en Jammable. Elige una voz y haz un cover con IA. Más de 93,000 voces en la biblioteca.",
    "library.crumb.home": "Inicio",
    "library.crumb.library": "Biblioteca de voces IA",
    "library.crumb.filter": "Voces {of}",

    "library.title": "Biblioteca de voces",
    "library.subtitle":
      "Más de 93,000 voces. Toca una para escucharla o haz un cover al instante.",
    "library.train": "Entrena tu propia voz",
    "library.search": "Buscar voces",
    "library.searchIn": "Buscar voces {of}",
    "library.categoriesNav": "Categorías de voces",
    "library.all": "Todas",
    "library.moreCategories": "Más categorías",
    "library.fewerCategories": "Menos categorías",
    "library.collections": "Colecciones",
    "library.collections.prev": "Colecciones anteriores",
    "library.collections.next": "Más colecciones",
    "library.collection.voices": "{n} voces",
    "library.voicesIn": "Voces {of}",
    "library.allVoices": "Todas las voces",
    "library.empty.title": "No encontramos voces",
    "library.empty.body": "Prueba con otro nombre o entrena la voz tú mismo.",
    "library.loadMore": "Cargar más voces",

    "library.sort.aria": "Ordenar voces",
    "library.sort.hot": "Tendencia hoy",
    "library.sort.hot.short": "Tendencia",
    "library.sort.top": "Más usadas",
    "library.sort.top.short": "Más usadas",
    "library.sort.liked": "Más gustadas",
    "library.sort.liked.short": "Más gustadas",
    "library.sort.new": "Más nuevas",
    "library.sort.new.short": "Nuevas",

    "library.hub.title": "Voces IA {of}: haz covers con IA | Jammable",
    "library.hub.titlePage": "Voces IA {of}, página {page} | Jammable",
    "library.hub.description":
      "Explora más de {count} voces IA {of} en Jammable. Elige cualquier voz para hacer un cover con IA o usar el cambiador de voz con IA.",
    "library.hub.descriptionPage":
      "Página {page} de {lastPage} de voces IA {of} en Jammable, ordenadas por usos. Elige cualquier voz para hacer un cover con IA o usar el cambiador de voz con IA.",
    "library.hub.name": "Voces IA {of}",
    "library.hub.crumbVoices": "Voces IA",
    "library.hub.voices": "Voces",
    "library.hub.page": "Página {page}",
    "library.hub.h1": "Voces IA {of}",
    "library.hub.intro":
      "Explora más de {count} voces IA {of}. Elige una para hacer un cover con IA o úsala para cambiar tu voz.",
    "library.hub.popular": "Voces {of} populares",
    "library.hub.mostUsedPage":
      "Voces {of} más usadas, página {page} de {lastPage}",
    "library.hub.noMatch": "Ninguna voz {of} coincide con tu búsqueda.",
    "library.hub.openLibrary": "Abrir en la biblioteca de voces",
    "library.hub.moreCategories": "Explora más categorías",
    "library.hub.faq": "Preguntas frecuentes",
    "library.hub.keepExploring": "Sigue explorando",
    "library.hub.moreWays": "Más formas de crear con Jammable.",
    "library.hub.link.aiCover": "Generador de covers con IA",
    "library.hub.link.voiceChanger": "Cambiador de voz con IA",
    "library.hub.link.allVoices": "Ver todas las voces IA",
    "library.hub.pages": "Páginas",
    "library.hub.first": "Primera",
    "library.hub.prev": "Anterior",
    "library.hub.next": "Siguiente",
    "library.hub.last": "Última",
    "library.hub.faq1.q": "¿Qué son las voces IA {of}?",
    "library.hub.faq1.a":
      "Las voces IA {of} son modelos de voz que puedes usar para hacer covers con IA o cambiar tu propia voz. Jammable tiene más de {count}, y cada día se agregan voces nuevas.",
    "library.hub.faq2.q": "¿Cómo uso una voz IA {of}?",
    "library.hub.faq2.a":
      "Abre cualquier voz {of}, agrega audio que tengas derecho a usar (o graba el tuyo) y presiona generar. Jammable convierte la voz principal a esa voz y te entrega el cover terminado en segundos.",
    "library.hub.faq3.q": "¿Cuántas voces {of} hay?",
    "library.hub.faq3.a":
      "Jammable tiene más de {count} voces IA {of}, y la biblioteca crece cada día a medida que la comunidad sube y entrena voces nuevas.",

    "library.cat.public-figure": "famosos",
    "library.cat.music": "música",
    "library.cat.cartoon": "caricaturas",
    "library.cat.gaming": "videojuegos",
    "library.cat.fictional": "personajes ficticios",
    "library.cat.anime": "anime",
    "library.cat.influencer": "influencers",
    "library.cat.singer": "cantantes",
    "library.cat.comedy": "comedia",
    "library.cat.pop": "pop",
    "library.cat.youtuber": "youtubers",
    "library.cat.tv-show": "series de TV",
    "library.cat.rap": "rap",
    "library.cat.fictional-character": "personajes ficticios",
    "library.cat.movie": "películas",
    "library.cat.movies": "películas",
    "library.cat.funny": "humor",
    "library.cat.kpop": "K-pop",
    "library.cat.k-pop": "K-pop",
    "library.cat.parody": "parodias",
    "library.cat.animation": "animación",
    "library.cat.animated": "animación",
    "library.cat.rock": "rock",
    "library.cat.vtuber": "VTubers",
    "library.cat.fantasy": "fantasía",
    "library.cat.game": "juegos",
    "library.cat.entertainment": "entretenimiento",
    "library.cat.rapper": "raperos",
    "library.cat.horror": "terror",
    "library.cat.actor": "actores",
    "library.cat.actors": "actores",
    "library.cat.actress": "actrices",
    "library.cat.meme": "memes",
    "library.cat.action": "acción",
    "library.cat.educational": "contenido educativo",
    "library.cat.hip-hop-rap": "hip-hop/rap",
    "library.cat.hip-hop": "hip-hop",
    "library.cat.adventure": "aventura",
    "library.cat.tv": "TV",
    "library.cat.female": "mujeres",
    "library.cat.male": "hombres",
    "library.cat.superhero": "superhéroes",
    "library.cat.drama": "drama",
    "library.cat.sci-fi": "ciencia ficción",
    "library.cat.science-fiction": "ciencia ficción",
    "library.cat.alternative": "música alternativa",
    "library.cat.metal": "metal",
    "library.cat.technology": "tecnología",
    "library.cat.indie": "indie",
    "library.cat.celebrity": "famosos",
    "library.cat.fiction": "ficción",
    "library.cat.american": "estadounidenses",
    "library.cat.british": "británicos",
    "library.cat.australian": "australianos",
    "library.cat.latino": "latinos",
    "library.cat.mexico": "México",
    "library.cat.villain": "villanos",
    "library.cat.politics": "política",
    "library.cat.president": "presidentes",
    "library.cat.animal": "animales",
    "library.cat.animals": "animales",
    "library.cat.dog": "perros",
    "library.cat.cat": "gatos",
    "library.cat.sports": "deportes",
    "library.cat.wrestling": "lucha libre",
    "library.cat.streamer": "streamers",
    "library.cat.country": "country",
    "library.cat.robot": "robots",
    "library.cat.sound-effects": "efectos de sonido",
    "library.cat.historical": "personajes históricos",
    "library.cat.folk": "folk",
    "library.cat.r-b": "R&B",
    "library.cat.rnb": "R&B",
    "library.cat.jazz": "jazz",
    "library.cat.reggaeton": "reguetón",
    "library.cat.electronic": "música electrónica",
    "library.cat.pop-music": "música pop",
    "library.cat.comic": "cómics",
    "library.cat.demon": "demonios",
    "library.cat.news": "noticias",
    "library.cat.artist": "artistas",
    "library.cat.artistas": "artistas",
    "library.cat.cantantes": "cantantes",
    "library.cat.cantante": "cantantes",
    "library.cat.personajes": "personajes",
    "library.cat.character": "personajes",
    "library.cat.singing": "canto",
    "library.cat.mystery": "misterio",
    "library.cat.christmas": "Navidad",
    "library.cat.sitcom": "sitcoms",
    "library.cat.pirate": "piratas",
    "library.cat.toy": "juguetes",
    "library.cat.classic": "clásicos",
    "library.cat.art": "arte",
    "library.cat.poetry": "poesía",
    "library.cat.food": "comida",
    "library.cat.fashion": "moda",
    "library.cat.family": "familia",
    "library.cat.podcast": "podcasts",
    "library.cat.radio": "radio",
    "library.cat.ai": "IA",
    "library.cat.artificial-intelligence": "inteligencia artificial",
    "library.cat.song": "canciones",
    "library.cat.ringtone": "tonos de llamada",
    "library.cat.web-series": "series web",
    "library.cat.idol": "idols",
    "library.cat.dub": "doblaje",
    "library.cat.80s": "los años 80",
    "library.cat.indie-games": "juegos indie",
    "library.cat.game-show": "concursos de TV",
    "library.lang.japanese": "japonés",
    "library.lang.japenese": "japonés",
    "library.lang.korean": "coreano",
    "library.lang.spanish": "español",
    "library.lang.english": "inglés",
    "library.lang.french": "francés",
    "library.lang.italian": "italiano",
    "library.lang.german": "alemán",
    "library.lang.deutsch": "alemán",
    "library.lang.russian": "ruso",
    "library.lang.hebrew": "hebreo",
    "library.lang.chinese": "chino",
    "library.lang.arabic": "árabe",
    "library.lang.swedish": "sueco",
    "library.lang.polish": "polaco",
    "library.lang.turkish": "turco",
    "library.lang.finnish": "finlandés",
    "library.lang.greek": "griego",
    "library.lang.indonesian": "indonesio",
    "library.lang.thai": "tailandés",
    "library.lang.hungarian": "húngaro",
    "library.lang.filipino": "filipino",
    "library.lang.malaysian": "malayo",
    "library.lang.portuguese": "portugués",
    "library.lang.brasileiro": "portugués de Brasil",
    "library.lang.pt-br": "portugués de Brasil",
    "library.lang.romanian": "rumano",
    "library.lang.hindi": "hindi",
    "library.lang.dutch": "neerlandés",
    "library.lang.czech": "checo",
    "library.lang.vietnamese": "vietnamita",
  },
  pt: {
    "library.of": "de {name}",
    "library.ofLanguage": "em {name}",

    "library.seo.title": "Biblioteca de vozes de IA{filter} | Jammable",
    "library.seo.titleFiltered":
      "Biblioteca de vozes de IA - {filter} | Jammable",
    "library.seo.description":
      "Explore mais de 93 mil vozes de IA. Faça covers com IA usando vozes de anime, games, desenhos, K-pop e muito mais. Vozes novas todos os dias.",
    "library.seo.ogDescription":
      "Explore mais de 93 mil vozes de IA. Faça covers com IA usando vozes de anime, games, desenhos, K-pop e muito mais.",
    "library.seo.listDescription":
      "Explore mais de 93 mil vozes de IA. Faça covers com IA usando vozes de anime, games, desenhos, K-pop e muito mais. Vozes novas todos os dias.",
    "library.seo.listDescriptionFiltered":
      "Vozes de IA {of} no Jammable. Escolha uma voz e faça um cover com IA. Mais de 93 mil vozes na biblioteca.",
    "library.crumb.home": "Início",
    "library.crumb.library": "Biblioteca de vozes de IA",
    "library.crumb.filter": "Vozes {of}",

    "library.title": "Biblioteca de vozes",
    "library.subtitle":
      "Mais de 93 mil vozes. Toque em uma para ouvir ou faça um cover na hora.",
    "library.train": "Treine sua própria voz",
    "library.search": "Buscar vozes",
    "library.searchIn": "Buscar vozes {of}",
    "library.categoriesNav": "Categorias de vozes",
    "library.all": "Todas",
    "library.moreCategories": "Mais categorias",
    "library.fewerCategories": "Menos categorias",
    "library.collections": "Coleções",
    "library.collections.prev": "Coleções anteriores",
    "library.collections.next": "Mais coleções",
    "library.collection.voices": "{n} vozes",
    "library.voicesIn": "Vozes {of}",
    "library.allVoices": "Todas as vozes",
    "library.empty.title": "Nenhuma voz encontrada",
    "library.empty.body": "Tente outro nome ou treine a voz você mesmo.",
    "library.loadMore": "Carregar mais vozes",

    "library.sort.aria": "Ordenar vozes",
    "library.sort.hot": "Em alta hoje",
    "library.sort.hot.short": "Em alta",
    "library.sort.top": "Mais usadas",
    "library.sort.top.short": "Mais usadas",
    "library.sort.liked": "Mais curtidas",
    "library.sort.liked.short": "Mais curtidas",
    "library.sort.new": "Mais recentes",
    "library.sort.new.short": "Recentes",

    "library.hub.title": "Vozes de IA {of}: faça covers com IA | Jammable",
    "library.hub.titlePage": "Vozes de IA {of}, página {page} | Jammable",
    "library.hub.description":
      "Explore mais de {count} vozes de IA {of} no Jammable. Escolha qualquer voz para fazer um cover com IA ou usar o modificador de voz com IA.",
    "library.hub.descriptionPage":
      "Página {page} de {lastPage} das vozes de IA {of} no Jammable, ordenadas por usos. Escolha qualquer voz para fazer um cover com IA ou usar o modificador de voz com IA.",
    "library.hub.name": "Vozes de IA {of}",
    "library.hub.crumbVoices": "Vozes de IA",
    "library.hub.voices": "Vozes",
    "library.hub.page": "Página {page}",
    "library.hub.h1": "Vozes de IA {of}",
    "library.hub.intro":
      "Explore mais de {count} vozes de IA {of}. Escolha uma para fazer um cover com IA ou use-a como modificador de voz.",
    "library.hub.popular": "Vozes {of} populares",
    "library.hub.mostUsedPage":
      "Vozes {of} mais usadas, página {page} de {lastPage}",
    "library.hub.noMatch": "Nenhuma voz {of} corresponde à sua busca.",
    "library.hub.openLibrary": "Abrir na biblioteca de vozes",
    "library.hub.moreCategories": "Explore mais categorias",
    "library.hub.faq": "Perguntas frequentes",
    "library.hub.keepExploring": "Continue explorando",
    "library.hub.moreWays": "Mais formas de criar com o Jammable.",
    "library.hub.link.aiCover": "Gerador de covers com IA",
    "library.hub.link.voiceChanger": "Modificador de voz com IA",
    "library.hub.link.allVoices": "Ver todas as vozes de IA",
    "library.hub.pages": "Páginas",
    "library.hub.first": "Primeira",
    "library.hub.prev": "Anterior",
    "library.hub.next": "Próxima",
    "library.hub.last": "Última",
    "library.hub.faq1.q": "O que são as vozes de IA {of}?",
    "library.hub.faq1.a":
      "As vozes de IA {of} são modelos de voz que você pode usar para fazer covers com IA ou mudar sua própria voz. O Jammable tem mais de {count}, e vozes novas chegam todos os dias.",
    "library.hub.faq2.q": "Como usar uma voz de IA {of}?",
    "library.hub.faq2.a":
      "Abra qualquer voz {of}, adicione um áudio que você tenha direito de usar (ou grave o seu) e toque em gerar. O Jammable converte os vocais para essa voz e entrega o cover pronto em segundos.",
    "library.hub.faq3.q": "Quantas vozes {of} existem?",
    "library.hub.faq3.a":
      "O Jammable tem hoje mais de {count} vozes de IA {of}, e a biblioteca cresce todo dia conforme a comunidade envia e treina novas vozes.",

    "library.cat.public-figure": "famosos",
    "library.cat.music": "música",
    "library.cat.cartoon": "desenhos",
    "library.cat.gaming": "games",
    "library.cat.fictional": "personagens fictícios",
    "library.cat.anime": "anime",
    "library.cat.influencer": "influenciadores",
    "library.cat.singer": "cantores",
    "library.cat.comedy": "comédia",
    "library.cat.pop": "pop",
    "library.cat.youtuber": "youtubers",
    "library.cat.tv-show": "séries de TV",
    "library.cat.rap": "rap",
    "library.cat.fictional-character": "personagens fictícios",
    "library.cat.movie": "filmes",
    "library.cat.movies": "filmes",
    "library.cat.funny": "humor",
    "library.cat.kpop": "K-pop",
    "library.cat.k-pop": "K-pop",
    "library.cat.parody": "paródias",
    "library.cat.animation": "animação",
    "library.cat.animated": "animação",
    "library.cat.rock": "rock",
    "library.cat.vtuber": "VTubers",
    "library.cat.fantasy": "fantasia",
    "library.cat.game": "jogos",
    "library.cat.entertainment": "entretenimento",
    "library.cat.rapper": "rappers",
    "library.cat.horror": "terror",
    "library.cat.actor": "atores",
    "library.cat.actors": "atores",
    "library.cat.actress": "atrizes",
    "library.cat.meme": "memes",
    "library.cat.action": "ação",
    "library.cat.educational": "conteúdo educativo",
    "library.cat.hip-hop-rap": "hip-hop/rap",
    "library.cat.hip-hop": "hip-hop",
    "library.cat.adventure": "aventura",
    "library.cat.tv": "TV",
    "library.cat.female": "mulheres",
    "library.cat.male": "homens",
    "library.cat.superhero": "super-heróis",
    "library.cat.drama": "drama",
    "library.cat.sci-fi": "ficção científica",
    "library.cat.science-fiction": "ficção científica",
    "library.cat.alternative": "música alternativa",
    "library.cat.metal": "metal",
    "library.cat.technology": "tecnologia",
    "library.cat.indie": "indie",
    "library.cat.celebrity": "famosos",
    "library.cat.fiction": "ficção",
    "library.cat.american": "americanos",
    "library.cat.british": "britânicos",
    "library.cat.australian": "australianos",
    "library.cat.latino": "latinos",
    "library.cat.mexico": "México",
    "library.cat.villain": "vilões",
    "library.cat.politics": "política",
    "library.cat.president": "presidentes",
    "library.cat.animal": "animais",
    "library.cat.animals": "animais",
    "library.cat.dog": "cachorros",
    "library.cat.cat": "gatos",
    "library.cat.sports": "esportes",
    "library.cat.wrestling": "luta livre",
    "library.cat.streamer": "streamers",
    "library.cat.country": "country",
    "library.cat.robot": "robôs",
    "library.cat.sound-effects": "efeitos sonoros",
    "library.cat.historical": "personagens históricos",
    "library.cat.folk": "folk",
    "library.cat.r-b": "R&B",
    "library.cat.rnb": "R&B",
    "library.cat.jazz": "jazz",
    "library.cat.reggaeton": "reggaeton",
    "library.cat.electronic": "música eletrônica",
    "library.cat.pop-music": "música pop",
    "library.cat.comic": "quadrinhos",
    "library.cat.demon": "demônios",
    "library.cat.news": "notícias",
    "library.cat.artist": "artistas",
    "library.cat.artistas": "artistas",
    "library.cat.cantantes": "cantores",
    "library.cat.cantante": "cantores",
    "library.cat.personajes": "personagens",
    "library.cat.character": "personagens",
    "library.cat.singing": "canto",
    "library.cat.mystery": "mistério",
    "library.cat.christmas": "Natal",
    "library.cat.sitcom": "sitcoms",
    "library.cat.pirate": "piratas",
    "library.cat.toy": "brinquedos",
    "library.cat.classic": "clássicos",
    "library.cat.art": "arte",
    "library.cat.poetry": "poesia",
    "library.cat.food": "comida",
    "library.cat.fashion": "moda",
    "library.cat.family": "família",
    "library.cat.podcast": "podcasts",
    "library.cat.radio": "rádio",
    "library.cat.ai": "IA",
    "library.cat.artificial-intelligence": "inteligência artificial",
    "library.cat.song": "músicas",
    "library.cat.ringtone": "toques de celular",
    "library.cat.web-series": "websséries",
    "library.cat.idol": "idols",
    "library.cat.dub": "dublagem",
    "library.cat.80s": "anos 80",
    "library.cat.indie-games": "jogos indie",
    "library.cat.game-show": "game shows",
    "library.lang.japanese": "japonês",
    "library.lang.japenese": "japonês",
    "library.lang.korean": "coreano",
    "library.lang.spanish": "espanhol",
    "library.lang.english": "inglês",
    "library.lang.french": "francês",
    "library.lang.italian": "italiano",
    "library.lang.german": "alemão",
    "library.lang.deutsch": "alemão",
    "library.lang.russian": "russo",
    "library.lang.hebrew": "hebraico",
    "library.lang.chinese": "chinês",
    "library.lang.arabic": "árabe",
    "library.lang.swedish": "sueco",
    "library.lang.polish": "polonês",
    "library.lang.turkish": "turco",
    "library.lang.finnish": "finlandês",
    "library.lang.greek": "grego",
    "library.lang.indonesian": "indonésio",
    "library.lang.thai": "tailandês",
    "library.lang.hungarian": "húngaro",
    "library.lang.filipino": "filipino",
    "library.lang.malaysian": "malaio",
    "library.lang.portuguese": "português",
    "library.lang.brasileiro": "português brasileiro",
    "library.lang.pt-br": "português brasileiro",
    "library.lang.romanian": "romeno",
    "library.lang.hindi": "hindi",
    "library.lang.dutch": "holandês",
    "library.lang.czech": "tcheco",
    "library.lang.vietnamese": "vietnamita",
  },
};
